import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  ControlValueAccessor,
  DefaultValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatFormFieldAppearance,
  MatFormFieldModule,
  MatLabel,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { ErrorFieldComponent } from '../error-field/error-field.component';

/**
 * @example
 *  <siat-input-field
 *    [formGroup]="formModificaStruttura"
 *    controlName="controlName"
 *    id="ID"
 *    name="Name"
 *    placeholder="Placeholder"
 *    label="Label"
 *    type="text"
 *  ></siat-input-field>
 */
@Component({
  selector: 'siat-input-field',
  templateUrl: './input-field.component.html',
  providers: [DefaultValueAccessor],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorFieldComponent,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatLabel,
    MatTooltip,
  ],
})
export class InputFieldComponent implements ControlValueAccessor {
  /**
   * The maxlength attribute for the input field.
   */
  @Input()
  maxlength?: number;
  /**
   * The form group associated with the input field.
   */
  @Input()
  formGroup: FormGroup;

  /**
   * The control name associated with the input field.
   */
  @Input()
  controlName: string;

  /**
   * The id attribute of the input field.
   */
  @Input()
  id?: string;

  /**
   * Th placeholder of the input field.
   */
  @Input()
  placeholder?: string;

  /**
   * The input label.
   */
  @Input()
  label: string;

  /**
   * The input type.
   */
  @Input()
  type: 'text' | 'number' | 'password' | 'email' = 'text';

  /**
   * The input "name" attribute value.
   */
  @Input()
  name?: string;

  /**
   * The input "autocomplete" attribute value.
   */
  @Input()
  autocomplete: string;

  /**
   * The input class.
   */
  @Input()
  fieldClass: string;

  /**
   * The input prefix.
   */
  @Input()
  prefix?: string;

  /**
   * The input appareance.
   */
  @Input()
  appearance: MatFormFieldAppearance = 'outline';

  /**
   * Indicates if the input field is a password field.
   */
  @Input()
  arePasswordField = false;

  @Input()
  unita?: string;

  /**
   * Indicates if the search icon is shown as suffix.
   */
  @Input()
  showSearchIcon = false;

  /**
   * Indicates if the input field is hidden.
   */
  hide = true;

  /**
   * Indicates if Caps Lock is active.
   * @internal
   */
  capsLockOn = false;

  /**
   * Gets the form control associated with the input field.
   */
  get control(): FormControl<string | number> {
    return this.formGroup.controls[this.controlName] as FormControl<
      string | number
    >;
  }

  /**
   * Gets a flag telling if the the form control is in a disabled state.
   */
  get disabled(): boolean {
    return this.control.disabled;
  }

  /**
   * @inheritdoc
   */
  onChange: (value: string | number) => void = () => {};

  /**
   * @inheritdoc
   */
  onTouched: () => void = () => {};

  /**
   * The write value function.
   */
  writeValue(obj: string | number): void {
    this.control.setValue(obj);
  }

  /**
   * The register on change function.
   */
  registerOnChange(changeCallback: (value: string | number) => void): void {
    this.onChange = changeCallback;
  }

  /**
   * The register on touched function.
   */
  registerOnTouched(touchedCallback: () => void): void {
    this.onTouched = touchedCallback;
  }

  /**
   * The toggle hide password function.
   */
  toggleHidePassword(): void {
    this.hide = !this.hide;
  }

  /**
   * Checks the state of the Caps Lock key and updates the `capsLockOn` property.
   */
  checkCapsLock(event: Event): void {
    if (!this.arePasswordField) {
      this.capsLockOn = false;
      return;
    }

    if (event instanceof KeyboardEvent) {
      this.capsLockOn = event.getModifierState('CapsLock');
    }
  }
}
